import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { apiURL } from "../config/config";
import ApprovalTable from "../components/ApprovalTable";
import LoadingOverlay from "../components/LoadingOverlay";
import PaginationControls from "../components/PaginationControls";
import SearchBar from "../components/SearchBar";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { quotationMatchesSearch } from "../utils/quotationSearch";

const ApprovalManagement = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [allQuotations, setAllQuotations] = useState([]);
  const [filteredQuotations, setFilteredQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const currentYear = new Date().getFullYear();
  const getPositiveNumber = (value, fallback) => {
    const number = Number(value);
    return Number.isInteger(number) && number > 0 ? number : fallback;
  };
  const [selectedYear, setSelectedYear] = useState(() =>
    getPositiveNumber(searchParams.get("year"), currentYear)
  );
  const [requestedPage, setRequestedPage] = useState(() =>
    getPositiveNumber(searchParams.get("page"), 1)
  );
  const [currentPage, setCurrentPage] = useState(() =>
    getPositiveNumber(searchParams.get("page"), 1)
  );
  const [totalPages, setTotalPages] = useState(1);

  const fetchApprovals = useCallback(async (year, page) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const user = JSON.parse(localStorage.getItem("user"));

      if (user && user.username) {
        const response = await axios.get(
          `${apiURL}quotations/approval-by-email/${encodeURIComponent(
            user.username
          )}?year=${year}&page=${page}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        const payload = response.data || {};
        const resolvedPage = Number(payload.currentPage || page || 1);
        setAllQuotations(Array.isArray(payload.data) ? payload.data : []);
        setCurrentPage(resolvedPage);
        setTotalPages(Number(payload.totalPages || 1));

        // The API can move an out-of-range page back to the last valid page.
        if (resolvedPage !== page) {
          setSearchParams(
            { year: String(year), page: String(resolvedPage) },
            { replace: true }
          );
        }
      } else {
        setAllQuotations([]);
        setFilteredQuotations([]);
        setTotalPages(1);
        setCurrentPage(1);
      }
    } catch (error) {
      console.error("Error fetching approvals:", error);
      toast.error("Failed to fetch approvals");
    } finally {
      setLoading(false);
    }
  }, [setSearchParams]);

  useEffect(() => {
    fetchApprovals(selectedYear, requestedPage);
  }, [selectedYear, requestedPage, fetchApprovals]);

  useEffect(() => {
    let result = allQuotations;

    if (searchTerm) {
      result = result.filter((q) => quotationMatchesSearch(q, searchTerm));
    }

    setFilteredQuotations(result);
  }, [searchTerm, allQuotations]);

  const handleChangeYear = (e) => {
    const year = Number(e.target.value);
    setSelectedYear(year);
    setRequestedPage(1);
    setCurrentPage(1);
    setSearchParams({ year: String(year), page: "1" });
  };

  const handlePageChange = (page) => {
    setRequestedPage(page);
    setCurrentPage(page);
    setSearchParams({ year: String(selectedYear), page: String(page) });
  };

  return (
    <div className="px-4 sm:px-6 py-6 relative min-h-screen dark:bg-gray-950 transition-colors duration-300">
      <LoadingOverlay loading={loading} />

      <div className="mb-8">
        <div>
          <h1 className="text-[26px] font-semibold text-gray-900 dark:text-yellow-400 leading-tight transition-colors">
            Approval Management
          </h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
            ตรวจสอบ อนุมัติ หรือปฏิเสธใบเสนอราคาที่รอการดำเนินการ
          </p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div className="flex flex-1 items-center gap-3">
          <select
            value={selectedYear}
            onChange={handleChangeYear}
            className="border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 rounded-md px-3 py-2 text-sm text-gray-700 dark:text-gray-200 focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 transition-all w-28"
          >
            {[currentYear + 1, currentYear, currentYear - 1, currentYear - 2].map(
              (y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              )
            )}
          </select>

          <div className="w-full sm:w-[500px]">
            <SearchBar
              searchTerm={searchTerm}
              setSearchTerm={(value) => {
                setSearchTerm(value);
              }}
              placeholder="ค้นหาใบเสนอราคา..."
            />
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-700 rounded-xl shadow-sm p-5 transition-colors duration-300">
        <div className="flex items-center gap-2 text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-5 h-5 text-yellow-500 dark:text-yellow-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 6h13M8 12h13m-13 6h13M3 6h.01M3 12h.01M3 18h.01"
            />
          </svg>
          <span>รายการรออนุมัติ</span>
        </div>

        <ApprovalTable quotations={filteredQuotations} />

        {totalPages > 1 && (
          <div className="mt-6">
            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ApprovalManagement;
